import './KbArticleEdit.css'
import { PUBLIC_WEBSITE } from '@/utilities/envVariables'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { kbArticleDeleteSpec, kbArticleGetSpec, kbArticleSaveSpec, type KbArticleRow } from 'aevocam-rest-api-spec'
import { KbArticleEditAssets } from './KbArticleEditAssets'
import { BasicPage } from '@/components/layout/BasicPage'
import { TextBox } from '@/components/scalar/TextBox'
import { CheckBox } from '@/components/scalar/CheckBox'
import { TextArea } from '@/components/scalar/TextArea'
import { Button } from '@/components/action/Button'
import { toastError, toastInfo, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { MarkdownViewer } from '@/components/scalar/MarkdownViewer'

export function KbArticleEdit() {
  const { id } = useParams<{ id: 'new' | string }>()
  const [queryParams] = useSearchParams()
  const location = useLocation()
  const api = useApi({ requireLogin: true, adminOnly: true })
  const navigate = useNavigate()

  const [article, setArticle] = useState<KbArticleRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [path, setPath] = useState('')
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [published, setPublished] = useState(false)
  const [publishAt, setPublishAt] = useState('')
  const [unpublishAt, setUnpublishAt] = useState('')
  const [contentPublic, setContentPublic] = useState('')

  const loadArticle = useCallback(async (value: KbArticleRow) => {
    setArticle(value)
    setPath(value.path ?? '')
    setTitle(value.title ?? '')
    setSubtitle(value.subtitle ?? '')
    setPublished(value.published ?? false)
    setPublishAt(value.publishAt ? String(value.publishAt) : '')
    setUnpublishAt(value.unpublishAt ? String(value.unpublishAt) : '')
    setContentPublic(value.contentPublic ?? '')
  }, [])

  useEffect(() => {
    async function fn() {
      if (id === 'new') {

        const article: KbArticleRow = {
          id: -1n,
          path: '/some-article',
          title: 'New article',
          subtitle: 'New article subtitle',
          published: false,
          publishAt: null,
          unpublishAt: null,
          contentPublic: 'Hi there!',
          contentDraft: null,
        }
        loadArticle(article)
        setLoading(false)
        return
      }  // id === 'new'

      setLoading(true)

      try {

        // Start with the article itself
        const res = await api.request(kbArticleGetSpec, {
          path: { article: id! },
          query: {
            forAdmin: 'true',
            path: queryParams.get('path') ?? undefined,
          },
        })

        if (res.status === 200 && res.data?.article) {
          const article = res.data.article
          loadArticle(article)
          const editUrl = routes.adminKbArticleEdit(String(article.id))
          if (editUrl !== location.pathname) {
            navigate(editUrl, { replace: true })
          }
        } else {
          toastError(res.error ?? 'Failed to load article')
        }

      } catch (error) {
        toastError('Failed to load article')
      } finally {
        setLoading(false)
      }
      
    }
    void fn()
  }, [id])

  async function handleSave() {
    if (!id || !article) return

    const res = await api.request(kbArticleSaveSpec, {
      path: { article: String(article.id) },
      body: {
        article: {
          ...article,
          path: path || null,
          title: title || null,
          subtitle: subtitle || null,
          published,
          publishAt: publishAt || null,
          unpublishAt: unpublishAt || null,
          contentPublic,
        }
      },
    })

    if (res.status === 200 && res.data?.article) {
      setArticle(res.data.article)
      const newUrl = routes.adminKbArticleEdit(String(res.data.article.id))
      if (id === 'new') {
        toastSuccess('New article saved')
        navigate(newUrl)
      } else {
        toastSuccess('Saved')
        window.history.pushState({}, '', newUrl)
      }
    } else {
      alert(res.error ?? 'Failed to save')
    }
  }

  function handleView() {
    const articlePath = (path || article?.path || '').trim()
    if (!articlePath) return
    let url = PUBLIC_WEBSITE + articlePath
    if (!article?.published)
      url += '?preview'
    window.open(url, '_blank')
  }

  async function handleDelete() {
    if (!id || id === 'new' || !article) return
    if (!confirm('Delete this article?')) return

    const res = await api.request(kbArticleDeleteSpec, {
      path: { article: id },
    })
    if (res.status === 200) {
      toastWarning('Deleted')
      navigate(routes.adminKbArticles())
    } else {
      toastError(res.error ?? 'Failed to delete')
    }
  }

  if (loading) return <main><p>Loading...</p></main>
  if (!article) return <main><p>Article not found</p></main>

  return (
    <BasicPage
      title={title ? 'Article: ' + title : 'Edit article'}
      className='KbArticleEdit'
      toolbarButtons={<>
        <Button
          label='Articles'
          icon='chevronLeft'
          href={routes.adminKbArticles()}
        />
        <Button
          label='New article'
          icon='plus'
          disabled={id === 'new'}
          href={routes.adminKbArticleNew()}
          onClick={() => toastInfo('New article')}
        />
        <Button
          variant='primary'
          label='Save'
          icon='save'
          onClick={handleSave}
        />
        <Button
          label='Delete'
          icon='trash'
          disabled={id === 'new'}
          onClick={handleDelete}
        />
        {id !== 'new' && (
          <Button
            label='View'
            icon='monitorPlay'
            tooltip='Opens a new tab'
            onClick={handleView}
          />
        )}
      </>}
    >

      <div className='Cutout Wrapped'>
        <TextBox
          label='Path'
          tooltip='/some-folder/and-another/the-article'
          required
          width={450}
          value={path}
          setValue={setPath}
        />
        <TextBox
          label='Title'
          required
          width={450}
          value={title}
          setValue={setTitle}
        />
        <TextBox
          label='Subtitle'
          width={450}
          value={subtitle}
          setValue={setSubtitle}
        />
        <CheckBox
          label='Published'
          tooltip='Whether this is visible to the public right now'
          value={published}
          setValue={setPublished}
        />
        {/*
        <TextBox
          label='Publish at'
          tooltip={`When to switch 'Published' on`}
          value={publishAt}
          setValue={setPublishAt}
        />
        <TextBox
          label='Unpublish at'
          tooltip={`When to switch 'Published' off`}
          value={unpublishAt}
          setValue={setUnpublishAt}
        />
        */}
      </div>

      {id !== 'new' && (article?.id ?? 0) > 0 && (
        <div className='Cutout'>
          <KbArticleEditAssets articleId={article.id!} />
        </div>
      )}

      <div className='Cutout FlexColumn Gap2'>

        <div className='EditAndView'>

          <div className='Editor'>
            <TextArea
              autoHeight
              monospace
              spellCheck
              value={contentPublic}
              setValue={setContentPublic}
            />
          </div>

          <div className='Viewer'>
            <MarkdownViewer
              value={contentPublic}
              articleId={article.id}
              withinEditor
              allowHtml
              allowPlugins
              linksToNewTabs
              localLinksTo={routes.adminKbArticleEdit('0') + '?path='}
              headersStartLevel={2}
            />
          </div>

        </div>

        <div className='ExtraViewer'>
          <MarkdownViewer
            value={contentPublic}
            articleId={article.id}
            withinEditor
            allowHtml
            linksToNewTabs
            localLinksTo={routes.adminKbArticleEdit('0') + '?path='}
            headersStartLevel={2}
          />
        </div>

      </div>

    </BasicPage>
  )
}
